import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NAV_GROUPS, NAV_TOP_LEVEL } from './navbar/navItems';
import NavGroupMenu from './navbar/NavGroupMenu';
import UserMenu from './navbar/UserMenu';
import MobileNav from './navbar/MobileNav';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout, isPro } = useAuth();

  const goToAuth = () => navigate('/auth');

  return (
    <nav
      className="fixed top-0 left-0 right-0 bg-[#07070d]/95 backdrop-blur-md border-b border-white/5 z-50"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="navbar-logo">
            <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 via-indigo-500 to-purple-600 flex items-center justify-center shadow-[0_0_18px_rgba(99,102,241,0.55)]">
              <span
                className="text-white font-extrabold text-lg"
                style={{ textShadow: '0 0 8px rgba(255,255,255,0.7)' }}
              >
                N
              </span>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-base font-bold text-white">Neon Alpha</span>
              <span className="text-[9px] uppercase tracking-[0.18em] text-white/40 whitespace-nowrap">
                Investment Club
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-3">
            {NAV_GROUPS.map((group) => (
              <NavGroupMenu
                key={group.label}
                group={group}
                pathname={location.pathname}
                isAuthed={!!user}
                isPro={isPro}
              />
            ))}
            {NAV_TOP_LEVEL.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium px-2 py-1.5 rounded-md transition-colors ${
                  location.pathname === item.path
                    ? 'text-cyan-300'
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            ))}

            {!loading && (
              <div className="flex items-center gap-3 pl-4 ml-2 border-l border-white/10">
                <UserMenu
                  user={user}
                  isPro={isPro}
                  onLogout={logout}
                  onGoToAuth={goToAuth}
                />
              </div>
            )}
          </div>

          <button
            type="button"
            className="md:hidden p-2 rounded-md text-white/80 hover:bg-white/5"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle navigation"
            data-testid="navbar-mobile-toggle"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <MobileNav
        isOpen={isOpen}
        pathname={location.pathname}
        user={user}
        isPro={isPro}
        loading={loading}
        onClose={() => setIsOpen(false)}
        onLogout={logout}
        onGoToAuth={goToAuth}
      />
    </nav>
  );
};

export default Navbar;
